import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faEnvelope, faLocationDot, faGlobe, faPalette, faImage } from '@fortawesome/free-solid-svg-icons';
import './Branding.scss';
import LogoWhite from '../../images/Logo-white.png';
import Logo from '../../images/Logo1.png';

/**
 * Branding component - Displays MET Connect® brand guidelines
 * 
 * This component shows brand colors, typography, logos and usage guidelines
 * and is intended for internal use or partners who need brand assets
 * 
 * @returns {JSX.Element} The Branding component
 */
const Branding = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Colors from the variables.scss file
  const brandColors = [
    { name: 'Primary', hex: '#0056b3', variable: '$primary-color', usage: 'Primary buttons, links, main accents' },
    { name: 'Secondary', hex: '#00a8e8', variable: '$secondary-color', usage: 'Secondary accents, highlights' },
    { name: 'Dark', hex: '#003559', variable: '$dark-color', usage: 'Text, dark backgrounds' },
    { name: 'Light', hex: '#f8f9fa', variable: '$light-color', usage: 'Light backgrounds' },
    { name: 'White', hex: '#ffffff', variable: '$white', usage: 'Text on dark backgrounds, cards' },
    { name: 'Black', hex: '#121212', variable: '$black', usage: 'Main text color' },
    { name: 'Gray', hex: '#6c757d', variable: '$gray', usage: 'Secondary text, borders' },
    { name: 'Success', hex: '#28a745', variable: '$success', usage: 'Success messages, indicators' },
    { name: 'Danger', hex: '#dc3545', variable: '$danger', usage: 'Error messages, alerts' }
  ];

  // Fonts information
  const fontFamilies = [
    { name: 'Inter', variable: '$font-family-base', usage: 'Base font for body text and general content', weights: '300, 400, 500, 600, 700' },
    { name: 'Montserrat', variable: '$font-family-headings', usage: 'Headings, titles, and important text', weights: '400, 500, 600, 700' },
    { name: 'Roboto Mono', variable: '$font-family-mono', usage: 'Code snippets, technical data, monospaced content', weights: '400, 500' },
    { name: 'Allura', variable: '$font-family-signature', usage: 'Signatures and handwritten elements', weights: '400' }
  ];

  // Font sizes
  const fontSizes = [
    { name: 'Base', variable: '$font-size-base', value: '1rem (16px)', usage: 'Standard body text' },
    { name: 'Large', variable: '$font-size-lg', value: '1.25rem (20px)', usage: 'Larger text, important information' },
    { name: 'Small', variable: '$font-size-sm', value: '0.875rem (14px)', usage: 'Small text, captions, notes' }
  ];

  // Font weights
  const fontWeights = [
    { name: 'Light', variable: '$font-weight-light', value: '300', usage: 'Light text, subtle emphasis' },
    { name: 'Normal', variable: '$font-weight-normal', value: '400', usage: 'Regular body text' },
    { name: 'Medium', variable: '$font-weight-medium', value: '500', usage: 'Medium emphasis, subheadings' },
    { name: 'Semibold', variable: '$font-weight-semibold', value: '600', usage: 'Strong emphasis, important content' },
    { name: 'Bold', variable: '$font-weight-bold', value: '700', usage: 'Headings, very strong emphasis' }
  ];

  return (
    <div className="branding-page">
      <div className="hero-section">
        <div className="hero-overlay"></div>
        <Container className="hero-content">
          <Row className="align-items-center">
            <Col lg={7} className="hero-text">
              <h1 className="hero-title">
                MET Connect<sup>®</sup> Brand Guidelines
              </h1>
            </Col>
            <div className="vertical-divider-container">
              <div className="vertical-divider"></div>
            </div>
            <Col>
              <p className="hero-subtitle">
                Essential branding resources and guidelines for maintaining consistent brand identity across all communications.
              </p>
              <div className="hero-features">
                <div className="hero-feature">
                  <FontAwesomeIcon icon={faPalette} className="feature-icon" />
                  <span>Brand Color Palette</span>
                </div>
                <div className="hero-feature">
                  <FontAwesomeIcon icon={faImage} className="feature-icon" />
                  <span>Logo Usage Guidelines</span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <section className="brand-section">
        <Container>
          <Row className="mb-5">
            <Col lg={12}>
              <h2>Brand Overview</h2>
              <p>
                The MET Connect brand represents security, reliability, and innovation in remote access and monitoring solutions.
                Our branding elements are designed to convey professionalism and technical expertise while maintaining a modern,
                clean appearance.
              </p>
              <p>
                Consistent application of these brand guidelines helps maintain brand recognition and builds trust with our customers and partners.
              </p>
            </Col>
          </Row>

          {/* Color Palette */}
          <Row className="mb-5">
            <Col lg={12}>
              <Card className="brand-card">
                <Card.Body>
                  <h3 className="section-title">Color Palette</h3>
                  <p>
                    The MET Connect® color palette consists of blues that convey trust, security, and professionalism,
                    complemented by neutral tones for balance and readability.
                  </p>

                  <Row className="color-palette">
                    {brandColors.map((color, index) => (
                      <Col key={index} md={4} lg={4} className="mb-4">
                        <div className="color-item">
                          <div
                            className="color-swatch"
                            style={{ backgroundColor: color.hex }}
                          />
                          <div className="color-details">
                            <h5>{color.name}</h5>
                            <p className="color-hex">{color.hex}</p>
                            <p className="color-var">{color.variable}</p>
                            <p className="color-usage">{color.usage}</p>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Typography */}
          <Row className="mb-5">
            <Col lg={12}>
              <Card className="brand-card">
                <Card.Body>
                  <h3 className="section-title">Typography</h3>
                  <p>
                    MET Connect® uses a combination of fonts that balance readability with modern design,
                    ensuring content is accessible across digital platforms.
                  </p>

                  <h4>Font Families</h4>
                  <Table responsive className="brand-table">
                    <thead>
                      <tr>
                        <th>Font</th>
                        <th>Variable</th>
                        <th>Usage</th>
                        <th>Weights</th>
                        <th>Sample</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fontFamilies.map((font, index) => (
                        <tr key={index}>
                          <td><strong>{font.name}</strong></td>
                          <td><code>{font.variable}</code></td>
                          <td>{font.usage}</td>
                          <td>{font.weights}</td>
                          <td>
                            <span className={`font-sample font-${font.name.toLowerCase().replace(' ', '-')}`}>
                              Aa Bb Cc 123
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  <Row className="mt-4">
                    <Col md={6}>
                      <h4>Font Sizes</h4>
                      <Table responsive className="brand-table">
                        <thead>
                          <tr>
                            <th>Size</th>
                            <th>Variable</th>
                            <th>Value</th>
                            <th>Usage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fontSizes.map((size, index) => (
                            <tr key={index}>
                              <td><strong>{size.name}</strong></td>
                              <td><code>{size.variable}</code></td>
                              <td>{size.value}</td>
                              <td>{size.usage}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={6}>
                      <h4>Font Weights</h4>
                      <Table responsive className="brand-table">
                        <thead>
                          <tr>
                            <th>Weight</th>
                            <th>Variable</th>
                            <th>Value</th>
                            <th>Usage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fontWeights.map((weight, index) => (
                            <tr key={index}>
                              <td><strong>{weight.name}</strong></td>
                              <td><code>{weight.variable}</code></td>
                              <td>{weight.value}</td>
                              <td>{weight.usage}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Col>
                  </Row>

                  <h4 className="mt-4">Heading Examples</h4>
                  <div className="heading-examples">
                    <h1>Heading 1 - Main Page Titles</h1>
                    <h2>Heading 2 - Section Titles</h2>
                    <h3>Heading 3 - Subsection Titles</h3>
                    <h4>Heading 4 - Content Headers</h4>
                    <h5>Heading 5 - Small Headers</h5>
                    <h6>Heading 6 - Minor Headers</h6>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Logos */}
          <Row className="mb-5">
            <Col lg={12}>
              <Card className="brand-card">
                <Card.Body>
                  <h3 className="section-title">Logo Usage</h3>
                  <p>
                    The MET Connect® logo is the primary visual identifier for our brand. It should be used consistently
                    and with proper spacing to maintain brand recognition.
                  </p>

                  <Row className="logo-examples">
                    <Col md={6} className="mb-4">
                      <div className="logo-container standard-bg">
                        <img src={Logo} alt="MET Connect® Logo" className="img-fluid logo-std" />
                        <p className="logo-caption">Standard logo - for light backgrounds</p>
                      </div>
                    </Col>
                    <Col md={6} className="mb-4">
                      <div className="logo-container dark-bg">
                        <img src={LogoWhite} alt="MET Connect White Logo" className="img-fluid logo-white" />
                        <p className="logo-caption">White logo - for dark backgrounds</p>
                      </div>
                    </Col>
                  </Row>

                  <h4>Logo Guidelines</h4>
                  <ul className="brand-guidelines">
                    <li>Maintain clear space around the logo equal to the height of the "M" in the logo</li>
                    <li>Never distort, rotate, or apply effects to the logo</li>
                    <li>Use the white version on dark backgrounds and the standard version on light backgrounds</li>
                    <li>Don't place the logo over busy backgrounds that reduce legibility</li>
                    <li>The minimum recommended size is 40px in height for digital usage</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Letterhead */}
          <Row className="mb-5">
            <Col lg={12}>
              <Card className="brand-card">
                <Card.Body>
                  <h3 className="section-title">Letterhead</h3>
                  <p>
                    MET Connect® letterhead maintains our professional brand identity across all official correspondence.
                    The design features our logo prominently while maintaining a clean, professional appearance.
                  </p>

                  <div className="letterhead-example">
                    <div className="letterhead">
                      <div className="letterhead-header">
                        <div className="letterhead-logo">
                          <img src={Logo} alt="MET Connect® Logo" />
                        </div>
                        <div className="letterhead-contact-info">
                          <div className="company-name">MET Connect®</div>
                          <div className="address">205/10 Albert Ave., BROADBEACH, QLD 4218, Australia</div>
                          <div className="contact-details">
                            <span className="phone">+61 (0)7 3333 23 53</span> |
                            <span className="website">www.met-connect.com</span> |
                            <span className="email">info@met-connect.com</span>
                          </div>
                        </div>
                      </div>
                      <div className="letterhead-content">
                        <div className="date">May 11, 2025</div>
                        <div className="recipient">
                          <p>John Doe</p>
                          <p>ABC Corporation</p>
                          <p>123 Business Avenue</p>
                          <p>Sydney, NSW 2000</p>
                        </div>
                        <div className="salutation">
                          <p>Dear Mr. Doe,</p>
                        </div>
                        <div className="body-text">
                          <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nunc euismod magna id libero facilisis, at elementum odio finibus. Mauris tincidunt urna eu magna placerat, vel tempor velit dignissim.</p>
                          <p>Sed tincidunt sem vel purus hendrerit, non commodo magna vestibulum. Donec elementum lacus in quam malesuada, ac facilisis neque tincidunt. Praesent efficitur diam eget semper fermentum.</p>
                        </div>
                        <div className="closing">
                          <p>Sincerely,</p>
                          <div className="signature">Iain Scott</div>
                          <div className="sender-title">Chief Executive Officer</div>
                          <div className="sender-email">i.scott@met-connect.com</div>
                        </div>
                      </div>
                      <div className="letterhead-footer">
                        <div className="footer-line"></div>
                        <div className="footer-text">
                          MET Connect® | ABN: 12 345 678 901 | 205/10 Albert Ave., BROADBEACH, QLD 4218, Australia
                        </div>
                      </div>
                    </div>
                    <div className="letterhead-download-section">
                      <h5>Letterhead Templates</h5>
                      <div className="download-links">
                        <div className="download-item">
                          <div className="download-icon">
                            <i className="fa fa-file-word-o"></i>
                          </div>
                          <div className="download-info">
                            <div className="download-title">Word Template</div>
                            <div className="download-desc">For Microsoft Word documents</div>
                          </div>
                        </div>
                        <div className="download-item">
                          <div className="download-icon">
                            <i className="fa fa-file-pdf-o"></i>
                          </div>
                          <div className="download-info">
                            <div className="download-title">PDF Template</div>
                            <div className="download-desc">For print or PDF conversion</div>
                          </div>
                        </div>
                      </div>
                      <p className="download-note">
                        Templates are available on the internal company server. Please contact the marketing department for access.
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Business Cards */}
          <Row className="mb-5">
            <Col lg={12}>
              <Card className="brand-card">
                <Card.Body>
                  <h3 className="section-title">Business Cards</h3>
                  <p>
                    MET Connect® business cards provide essential contact information in a format that reflects our brand identity.
                    Cards feature our logo, individual contact details, and a clean, professional design.
                  </p>

                  <div className="business-card-examples">
                    <Row>
                      <Col md={6} className="mb-4">
                        <div className="business-card-container">
                          <h4 className="mb-3">Front</h4>
                          <div className="business-card front">
                            <div className="business-card-logo-wrapper">
                              <div className="business-card-logo">
                                <img src={Logo} alt="MET Connect® Logo" />
                              </div>
                              <div className="text-logo">
                                <div className="logo-main-text">MET Connect<sup>®</sup></div>
                                <div className="logo-sub-text">Connected Since 2010</div>
                              </div>
                            </div>
                            <div className="business-card-details">
                              <div className="business-card-name">Iain Scott</div>
                              <div className="business-card-title">Chief Executive Officer</div>
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col md={6} className="mb-4">
                        <div className="business-card-container">
                          <h4 className="mb-3">Back</h4>
                          <div className="business-card back">
                            <div className="business-card-contact-details">
                              <div className="business-card-phone">
                                <span className="icon">
                                  <FontAwesomeIcon icon={faPhone} />
                                </span> +61 (0)7 3333 23 53
                              </div>
                              <div className="business-card-email">
                                <span className="icon">
                                  <FontAwesomeIcon icon={faEnvelope} />
                                </span> iscott@met-connect.com
                              </div>
                              <div className="business-card-address">
                                <span className="icon">
                                  <FontAwesomeIcon icon={faLocationDot} />
                                </span> 205/10 Albert Ave.,<br/>BROADBEACH, QLD 4218, Australia
                              </div>
                              <div className="business-card-website">
                                <span className="icon">
                                  <FontAwesomeIcon icon={faGlobe} />
                                </span> www.met-connect.com
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="business-card-specs mt-4">
                      <h4>Business Card Specifications</h4>
                      <Table responsive className="brand-table">
                        <tbody>
                          <tr>
                            <td><strong>Size</strong></td>
                            <td>90mm × 55mm (standard)</td>
                          </tr>
                          <tr>
                            <td><strong>Orientation</strong></td>
                            <td>Landscape</td>
                          </tr>
                          <tr>
                            <td><strong>Paper Stock</strong></td>
                            <td>350gsm matte laminated</td>
                          </tr>
                          <tr>
                            <td><strong>Colors</strong></td>
                            <td>Front: Full color | Back: Full color</td>
                          </tr>
                          <tr>
                            <td><strong>Finish</strong></td>
                            <td>Matte with spot UV on logo</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Email Signature */}
          <Row className="mb-5">
            <Col lg={12}>
              <Card className="brand-card">
                <Card.Body>
                  <h3 className="section-title">Email Signature</h3>
                  <p>
                    Consistent email signatures help reinforce our brand identity and provide recipients
                    with complete contact information.
                  </p>

                  <div className="email-signature-example">
                    <div className="email-signature">
                      <div className="email-name">Iain Scott</div>
                      <div className="email-title">Chief Executive Officer</div>
                      <div className="email-divider"></div>
                      <div className="email-company-wrapper">
                        <div className="email-logo">
                          <img src={Logo} alt="MET Connect® Logo" height="20" />
                        </div>
                        <div className="email-company">MET Connect<sup>®</sup></div>
                      </div>
                      <div className="email-contact">
                        <span className="email-phone">+61 (0)7 3333 23 53</span> |
                        <span className="email-email">iscott@met-connect.com</span>
                      </div>
                      <div className="email-address">
                        205/10 Albert Ave., BROADBEACH, QLD 4218, Australia
                      </div>
                      <div className="email-website">
                        <a href="https://www.met-connect.com">www.met-connect.com</a>
                      </div>
                      <div className="email-disclaimer">
                        This email and any files transmitted with it are confidential and intended solely for the use of the individual or entity to whom they are addressed.
                        If you have received this email in error, please notify the system manager.
                      </div>
                    </div>
                    <div className="email-code-block">
                      <h5>HTML Code</h5>
                      <div className="code-container">
                        <pre className="email-html-code">
{`<div style="font-family: Arial, sans-serif; color: #003559; line-height: 1.4;">
  <div style="font-weight: bold; font-size: 16px;">Iain Scott</div>
  <div style="color: #6c757d; font-size: 14px;">Chief Executive Officer</div>
  <div style="height: 1px; background-color: #0056b3; width: 200px; margin: 8px 0;"></div>
  <div style="display: flex; align-items: center;">
    <img src="[IMAGE_URL_HERE]" alt="MET Connect® Logo" height="20" />
    <span style="font-weight: bold; color: #003559; font-size: 16px; margin-left: 8px;">MET Connect®</span>
  </div>
  <div style="font-size: 14px; margin: 4px 0;">
    <span>+61 (0)7 3333 23 53</span> |
    <span><a href="mailto:iscott@met-connect.com" style="color: #0056b3; text-decoration: none;">iscott@met-connect.com</a></span>
  </div>
  <div style="font-size: 14px;">205/10 Albert Ave., BROADBEACH, QLD 4218, Australia</div>
  <div style="margin: 4px 0;">
    <a href="https://www.met-connect.com" style="color: #0056b3; text-decoration: none;">www.met-connect.com</a>
  </div>
  <div style="font-size: 10px; color: #6c757d; margin-top: 8px; max-width: 500px;">
    This email and any files transmitted with it are confidential and intended solely for the use of the individual 
    or entity to whom they are addressed. If you have received this email in error, please notify the system manager.
  </div>
</div>`}
                        </pre>
                      </div>
                      <p className="mt-3">Replace <code>[IMAGE_URL_HERE]</code> with the URL to your hosted logo image.</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Branding;